import React, { useCallback, useEffect, useState } from 'react';
import { getUiLocale } from '@/lib/dates';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Topbar } from '@/components/layout/Topbar';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Plus, Loader2, MessageSquare, XCircle, CheckCircle2 } from 'lucide-react';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { clientApi } from '@/lib/adminApi';
import { formatApiError } from '@/lib/api';
import { suiteDisplayName } from '@/lib/i18nTitles';
import { toast } from 'sonner';
import { ClientEvaluationRequestDialog } from '@/components/client/ClientEvaluationRequestDialog';
import { ClientSelfServeAssignDialog } from '@/components/client/ClientSelfServeAssignDialog';
import ConversationThread from '@/components/admin/ConversationThread';

const STATUS_VARIANT = {
  pending:  'secondary',
  approved: 'default',
  resolved: 'default',
  rejected: 'destructive',
  cancellation_requested: 'outline',
  cancelled: 'outline',
};

// Founder P0 2026-09 (Client Autonomy) — legacy pending requests can be
// continued in self-service. Suites the launcher assigns directly vs suites
// with their own self-service module the client is routed to.
const SUITE_MODULE_ROUTES = {
  talenttrain: '/client/talenttrain',
  talentcompetency: '/client/talentcompetency',
  talentcheck: '/client/talentcheck',
  climara: '/client/climara',
};

const URGENCY_TONE = {
  low:     'bg-muted text-muted-foreground',
  medium:  'bg-info/10 text-info',
  high:    'bg-warning/10 text-warning',
  urgent:  'bg-destructive/10 text-destructive',
};

function fmtDate(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(getUiLocale()); } catch { return iso; }
}

export default function ClientRequests() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [launchOpen, setLaunchOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  // UAT Sprint 02C · Item 8 — conversation drawer state (visible to client_admin,
  // wired to the pre-existing POST/GET /client/evaluation-requests/{id}/messages
  // endpoints so the same conversation array the Advisor sees in AdminLeads is
  // rendered here).
  const [msgReq, setMsgReq] = useState(null);
  const [msgLoading, setMsgLoading] = useState(false);
  const [msgSaving, setMsgSaving] = useState(false);
  // Founder UAT Wave 2 — audit-safe cancellation of pending requests.
  const [cancelReq, setCancelReq] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelSaving, setCancelSaving] = useState(false);
  // Founder P0 2026-09 — legacy pending request → self-service continuation.
  const [legacyReq, setLegacyReq] = useState(null);
  const [resolvingId, setResolvingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const items = await clientApi.listEvaluationRequests();
      setRows(items || []);
    } catch (e) {
      toast.error(t('pages.clientRequests.loadFailed'), { description: formatApiError(e) });
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { load(); }, [load]);

  const openMessages = async (rid) => {
    setMsgLoading(true);
    try {
      const fresh = await clientApi.getEvaluationRequest(rid);
      setMsgReq(fresh);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setMsgLoading(false);
    }
  };

  const sendMessage = async (body) => {
    if (!msgReq) return;
    setMsgSaving(true);
    try {
      await clientApi.postEvaluationRequestMessage(msgReq.id, body);
      const fresh = await clientApi.getEvaluationRequest(msgReq.id);
      setMsgReq(fresh);
      // Refresh the outer list so the "Messages (N)" pill increments.
      load();
    } finally {
      setMsgSaving(false);
    }
  };

  const statusLabel = (s) =>
    s === 'approved' ? t('pages.clientRequests.statusApproved')
    : s === 'resolved' ? t('pages.clientRequests.statusResolved', { defaultValue: 'Resuelta (autogestión)' })
    : s === 'rejected' ? t('pages.clientRequests.statusRejected')
    : s === 'cancellation_requested' ? t('pages.clientRequests.statusCancellationRequested', { defaultValue: 'Cancellation requested' })
    : s === 'cancelled' ? t('pages.clientRequests.statusCancelled', { defaultValue: 'Cancelled' })
    : t('pages.clientRequests.statusPending');

  // Legacy pending request → continue via the current self-service model.
  // The record is only resolved when the underlying action actually happened:
  // talentmap resolves after a REAL assignment; other suites navigate to the
  // module and offer an explicit "mark as resolved" declaration.
  const continueLegacy = (r) => {
    const code = (r.suite_code || '').toLowerCase();
    if (code === 'talentmap') {
      setLegacyReq(r); // direct launcher; auto-resolves only after assignment succeeds
      return;
    }
    const route = SUITE_MODULE_ROUTES[code] || '/client';
    toast.info(t('pages.clientRequests.continueHint', { defaultValue: 'Gestiona la acción en la suite y luego marca la solicitud como resuelta.' }));
    navigate(route);
  };

  const resolveLegacy = async (r) => {
    const ok = window.confirm(t('pages.clientRequests.resolveConfirm', {
      defaultValue: '¿Confirmas que la acción solicitada ya fue gestionada en autoservicio? La solicitud quedará como Resuelta (el registro se conserva).',
    }));
    if (!ok) return;
    setResolvingId(r.id);
    try {
      await clientApi.resolveSelfService(r.id);
      toast.success(t('pages.clientRequests.resolvedToast', { defaultValue: 'Solicitud resuelta. Continúa la gestión directamente en la suite.' }));
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setResolvingId(null); }
  };

  const submitCancellation = async () => {
    if (!cancelReq || cancelReason.trim().length < 3) return;
    setCancelSaving(true);
    try {
      await clientApi.requestCancellation(cancelReq.id, cancelReason.trim());
      toast.success(t('pages.clientRequests.cancelSuccess', { defaultValue: 'Cancellation requested. Your advisor will review it.' }));
      setCancelReq(null);
      setCancelReason('');
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setCancelSaving(false); }
  };

  return (
    <>
      <Topbar
        title={t('pages.clientRequests.title')}
        subtitle={t('pages.clientRequests.subtitle')}
        actions={
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => setOpen(true)}
              data-testid="creq-new-btn"
            >
              {t('pages.clientRequests.requestSupport', { defaultValue: 'Solicitar apoyo de CTH' })}
            </Button>
            <Button
              size="sm"
              className="bg-gradient-primary hover:opacity-90 gap-1.5"
              onClick={() => setLaunchOpen(true)}
              data-testid="creq-launch-btn"
            >
              <Plus className="h-4 w-4" /> {t('pages.clientRequests.launchSelfServe', { defaultValue: 'Lanzar evaluación (autogestión)' })}
            </Button>
          </div>
        }
      />
      <div className="p-6 lg:p-8 space-y-5">
        <Card className="shadow-card overflow-hidden" data-testid="creq-list-card">
          <div className="px-5 py-4 border-b border-border/60">
            <h3 className="font-semibold text-base">{t('pages.clientRequests.myRequestsTitle')}</h3>
            <p className="text-xs text-muted-foreground">{t('pages.clientRequests.myRequestsSubtitle')}</p>
          </div>
          {loading ? (
            <div className="py-12 flex items-center justify-center text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin mr-2" /> {t('common.loading')}
            </div>
          ) : rows.length === 0 ? (
            <div className="py-12 text-sm text-muted-foreground text-center" data-testid="creq-empty">
              {t('pages.clientRequests.myRequestsEmpty')}
            </div>
          ) : (
            <Table data-testid="creq-table">
              <TableHeader className="bg-muted/40">
                <TableRow>
                  <TableHead>{t('pages.clientRequests.rowSuite')}</TableHead>
                  <TableHead>{t('pages.clientRequests.rowCollabs')}</TableHead>
                  <TableHead className="hidden md:table-cell">{t('pages.clientRequests.position')}</TableHead>
                  <TableHead className="hidden md:table-cell">{t('pages.clientRequests.rowUrgency')}</TableHead>
                  <TableHead className="hidden lg:table-cell">{t('pages.clientRequests.rowRequestedAt')}</TableHead>
                  <TableHead>{t('pages.clientRequests.rowStatus')}</TableHead>
                  <TableHead className="text-right">
                    {t('pages.clientRequests.rowMessages', { defaultValue: 'Messages' })}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r) => {
                  const msgCount = (r.conversation || []).length;
                  return (
                  <TableRow key={r.id} className="hover:bg-muted/30" data-testid={`creq-row-${r.id}`}>
                    <TableCell className="font-medium">{suiteDisplayName(r.suite_code)}</TableCell>
                    <TableCell className="text-sm">
                      {(r.collaborators || []).map((c) => c.name || c.email).join(', ')
                        || (r.candidate && r.candidate.name)
                        || '—'}
                    </TableCell>
                    <TableCell className="hidden md:table-cell text-sm text-muted-foreground">
                      {r.position || '—'}
                    </TableCell>
                    <TableCell className="hidden md:table-cell">
                      <span className={`inline-flex items-center px-2 py-0.5 text-xs rounded-full ${URGENCY_TONE[r.urgency] || ''}`}>
                        {t(`pages.clientRequests.urgency${(r.urgency || 'medium').charAt(0).toUpperCase()}${(r.urgency || 'medium').slice(1)}`)}
                      </span>
                    </TableCell>
                    <TableCell className="hidden lg:table-cell text-sm text-muted-foreground">
                      {fmtDate(r.created_at)}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col items-start gap-1">
                        <Badge variant={STATUS_VARIANT[r.status] || 'secondary'} data-testid={`creq-status-${r.id}`}>
                          {statusLabel(r.status)}
                        </Badge>
                        {r.recruitment_process_id && (
                          <Button
                            variant="link" size="sm" className="h-auto p-0 text-xs"
                            data-testid={`creq-view-process-${r.id}`}
                            onClick={(e) => { e.stopPropagation(); navigate(`/client/recruitment/${r.recruitment_process_id}`); }}
                          >
                            {t('pages.clientRequests.viewProcess', { defaultValue: 'View process →' })}
                          </Button>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        {r.status === 'pending' && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => continueLegacy(r)}
                            data-testid={`creq-continue-${r.id}`}
                            className="gap-1 h-8"
                          >
                            <Plus className="h-3.5 w-3.5" />
                            <span className="text-xs">
                              {t('pages.clientRequests.continueSelfServe', { defaultValue: 'Continuar en autogestión' })}
                            </span>
                          </Button>
                        )}
                        {r.status === 'pending' && (r.suite_code || '').toLowerCase() !== 'talentmap' && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => resolveLegacy(r)}
                            disabled={resolvingId === r.id}
                            data-testid={`creq-resolve-${r.id}`}
                            className="gap-1 h-8"
                          >
                            {resolvingId === r.id
                              ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                              : <CheckCircle2 className="h-3.5 w-3.5" />}
                            <span className="text-xs">
                              {t('pages.clientRequests.resolveSelfServe', { defaultValue: 'Marcar como resuelta' })}
                            </span>
                          </Button>
                        )}
                        {r.status === 'pending' && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => { setCancelReq(r); setCancelReason(''); }}
                            data-testid={`creq-cancel-${r.id}`}
                            className="gap-1 h-8 text-destructive hover:text-destructive"
                          >
                            <XCircle className="h-3.5 w-3.5" />
                            <span className="hidden lg:inline text-xs">
                              {t('pages.clientRequests.requestCancel', { defaultValue: 'Request cancellation' })}
                            </span>
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openMessages(r.id)}
                          data-testid={`creq-messages-${r.id}`}
                          className="gap-1 h-8"
                        >
                          <MessageSquare className="h-3.5 w-3.5" />
                          <span className="tabular-nums">{msgCount}</span>
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </Card>

        <ClientEvaluationRequestDialog
          open={open}
          onOpenChange={setOpen}
          onCreated={() => load()}
        />

        {/* Client Autonomy — generic self-service launcher. Direct assignment
             for eligible suites; routes into the suite-specific self-service
             flow when the suite has its own configuration. */}
        <ClientSelfServeAssignDialog
          open={launchOpen}
          onOpenChange={setLaunchOpen}
          suite="talentmap"
          allowSuitePick
          onCreated={() => load()}
        />

        {/* Founder P0 2026-09 — legacy pending request continuation: direct
             launcher pre-filled with the originally requested collaborators.
             On success the legacy record is resolved (kept for history). */}
        <ClientSelfServeAssignDialog
          open={!!legacyReq}
          onOpenChange={(o) => { if (!o) setLegacyReq(null); }}
          suite={legacyReq?.suite_code || 'talentmap'}
          presetUserIds={legacyReq?.collaborator_ids || []}
          onCreated={async () => {
            try { await clientApi.resolveSelfService(legacyReq.id); } catch { /* keep pending if resolve fails */ }
            setLegacyReq(null);
            load();
          }}
        />

        {/* UAT Sprint 02C · Item 8 — client-side conversation drawer.
            Reuses the ConversationThread component that already powers
            AdminLeads, wired to the pre-existing client message endpoints. */}
        <Dialog
          open={!!msgReq}
          onOpenChange={(o) => { if (!o) setMsgReq(null); }}
        >
          <DialogContent className="max-w-2xl" data-testid="creq-messages-dialog">
            <DialogHeader>
              <DialogTitle>
                {t('pages.clientRequests.messagesTitle', { defaultValue: 'Request conversation' })}
              </DialogTitle>
              <DialogDescription>
                {msgReq
                  ? `${msgReq.suite_code} · ${msgReq.position || '—'}`
                  : ''}
              </DialogDescription>
            </DialogHeader>
            {msgLoading ? (
              <div className="py-10 flex items-center justify-center text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin mr-2" />{t('common.loading')}
              </div>
            ) : msgReq ? (
              <>
                {/* Founder UX sprint — the request keeps communicating after decision */}
                <div className="rounded-md border border-primary/20 bg-primary/[0.04] px-3 py-2 text-xs text-muted-foreground leading-relaxed" data-testid="creq-status-explainer">
                  {(() => {
                    const isES = (i18n.language || 'es').toLowerCase().startsWith('es');
                    const M = {
                      pending: isES
                        ? 'Tu solicitud de apoyo fue recibida por el equipo CTH y te responderán por este hilo. Si prefieres no esperar, puedes continuar en autogestión desde la tabla de solicitudes.'
                        : 'Your support request was received by the CTH team and they will reply in this thread. If you prefer not to wait, you can continue via self-service from the requests table.',
                      approved: isES
                        ? 'Aprobada — el equipo CTH está preparando la evaluación. El participante recibirá su invitación y el informe final aparecerá en Informes cuando se publique.'
                        : 'Approved — the CTH team is preparing the evaluation. The participant will receive their invitation and the final report will appear in Reports once released.',
                      resolved: isES
                        ? 'Resuelta en autogestión — esta solicitud del modelo anterior fue gestionada directamente por tu organización. El registro se conserva como historial.'
                        : 'Resolved via self-service — this legacy request was handled directly by your organization. The record is kept as history.',
                      rejected: isES
                        ? 'Revisada con comentarios — consulta el motivo en este hilo. Puedes crear una nueva solicitud con los ajustes indicados.'
                        : 'Reviewed with comments — see the reason in this thread. You can create a new request with the suggested adjustments.',
                      cancellation_requested: isES
                        ? 'Solicitaste la cancelación. El equipo CTH debe confirmarla; verás la decisión aquí.'
                        : 'You requested cancellation. The CTH team must confirm it; you will see the decision here.',
                      cancelled: isES ? 'Solicitud cancelada. No habrá más acciones.' : 'Request cancelled. No further actions.',
                    };
                    return M[msgReq.status] || M.pending;
                  })()}
                </div>
                <ConversationThread
                  messages={msgReq.conversation || []}
                  onSend={sendMessage}
                  disabled={msgSaving}
                />
              </>
            ) : null}
          </DialogContent>
        </Dialog>

        {/* Founder UAT Wave 2 — request-cancellation dialog (audit trail, no hard delete) */}
        <Dialog open={!!cancelReq} onOpenChange={(o) => { if (!o) setCancelReq(null); }}>
          <DialogContent className="max-w-md" data-testid="creq-cancel-dialog">
            <DialogHeader>
              <DialogTitle>{t('pages.clientRequests.cancelDialogTitle', { defaultValue: 'Request cancellation' })}</DialogTitle>
              <DialogDescription>
                {t('pages.clientRequests.cancelDialogDesc', { defaultValue: 'El equipo CTH revisará esta cancelación. El registro se conserva para fines de auditoría.' })}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-1.5">
              <Label>{t('pages.clientRequests.cancelReasonLabel', { defaultValue: 'Reason' })}</Label>
              <Textarea
                rows={3}
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                placeholder={t('pages.clientRequests.cancelReasonPh', { defaultValue: 'Why should this request be cancelled?' })}
                data-testid="creq-cancel-reason"
              />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCancelReq(null)} disabled={cancelSaving}>
                {t('common.cancel', { defaultValue: 'Cancel' })}
              </Button>
              <Button
                variant="destructive"
                onClick={submitCancellation}
                disabled={cancelSaving || cancelReason.trim().length < 3}
                data-testid="creq-cancel-submit"
              >
                {cancelSaving && <Loader2 className="h-4 w-4 animate-spin mr-1" />}
                {t('pages.clientRequests.cancelSubmit', { defaultValue: 'Submit cancellation request' })}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </>
  );
}
